import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { Joyride, TooltipRenderProps } from 'react-joyride';
import { useTutorial } from '@/hooks/useTutorial';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

// Contexto del tutorial
interface TutorialContextType {
  isRunning: boolean;
  startTutorial: () => void;
  endTutorial: () => void;
  resetTutorial: () => void;
  shouldShowTutorial: boolean;
  isCompleted: boolean;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export function useTutorialContext() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within TutorialProvider');
  }
  return context;
}

// Componente de tooltip personalizado
function CustomTooltip({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
}: TooltipRenderProps) {
  const isFirstStep = index === 0;
  const isLastStep = index === size - 1;
  const isCenterStep = step.placement === 'center';

  return (
    <Card
      {...tooltipProps}
      className="max-w-md shadow-2xl border-2 border-[#B8860B]/30 bg-white animate-in fade-in-0 zoom-in-95 duration-300"
    >
      <CardHeader className="pb-2 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isCenterStep && (
              <div className="p-2 bg-[#B8860B]/10 rounded-full">
                <Sparkles className="h-5 w-5 text-[#B8860B]" />
              </div>
            )}
            <CardTitle className="text-lg font-bold text-neutral-900">
              {step.title}
            </CardTitle>
          </div>
          <Button
            {...closeProps}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Indicador de progreso */}
        <div className="flex gap-1 mt-2">
          {Array.from({ length: size }, (_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= index ? 'bg-[#B8860B]' : 'bg-neutral-200'
              }`}
            />
          ))}
        </div>
      </CardHeader>

      <CardContent className="py-4">
        <p className="text-neutral-600 leading-relaxed">{step.content}</p>
      </CardContent>

      <CardFooter className="pt-2 flex justify-between items-center border-t">
        <div className="flex items-center gap-2">
          {!isLastStep && (
            <Button
              {...skipProps}
              variant="ghost"
              size="sm"
              className="text-neutral-500 hover:text-neutral-700"
            >
              Saltar tutorial
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <Button
              {...backProps}
              variant="outline"
              size="sm"
              className="border-neutral-300"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Atrás
            </Button>
          )}

          <Button
            {...primaryProps}
            size="sm"
            className="bg-[#B8860B] hover:bg-[#996F0A] text-white"
          >
            {isLastStep ? (
              '¡Empezar!'
            ) : isFirstStep ? (
              '¡Comenzar tour!'
            ) : (
              <>
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </CardFooter>

      {/* Contador de pasos */}
      <div className="px-4 pb-3 text-center">
        <span className="text-xs text-neutral-400">
          Paso {index + 1} de {size}
        </span>
      </div>
    </Card>
  );
}

// Modal de bienvenida para iniciar el tutorial
function WelcomeModal({
  onStart,
  onSkip,
}: {
  onStart: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 animate-in fade-in-0 duration-300">
      <Card className="max-w-lg w-full shadow-2xl border-2 border-[#B8860B]/30 animate-in zoom-in-95 duration-300">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 p-4 bg-gradient-to-br from-[#B8860B]/20 to-[#D4AF37]/20 rounded-full w-fit">
            <Sparkles className="h-12 w-12 text-[#B8860B]" />
          </div>
          <CardTitle className="text-2xl font-bold text-neutral-900">
            ¡Bienvenido a BeautyBook!
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-4">
          <p className="text-neutral-600 text-lg">
            Parece que es tu primera vez aquí. ¿Te gustaría hacer un tour rápido para conocer todas las funciones de tu plataforma?
          </p>

          <div className="flex flex-wrap justify-center gap-3 text-sm text-neutral-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-[#B8860B] rounded-full"></span>
              Gestión de citas
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-[#B8860B] rounded-full"></span>
              Control de clientes
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-[#B8860B] rounded-full"></span>
              Inventario y pagos
            </span>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 pt-4">
          <Button
            onClick={onStart}
            className="w-full bg-[#B8860B] hover:bg-[#996F0A] text-white py-6 text-lg"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            ¡Sí, empezar tour!
          </Button>

          <Button
            onClick={onSkip}
            variant="ghost"
            className="w-full text-neutral-500 hover:text-neutral-700"
          >
            Saltar por ahora, ya conozco la plataforma
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

interface TutorialProviderProps {
  children: ReactNode;
}

export function TutorialProvider({ children }: TutorialProviderProps) {
  const {
    isRunning,
    stepIndex,
    steps,
    startTutorial,
    endTutorial,
    resetTutorial,
    handleCallback,
    shouldShowTutorial,
    isCompleted,
  } = useTutorial();

  const [showWelcome, setShowWelcome] = useState(false);

  // Mostrar modal de bienvenida si debería mostrar tutorial
  useEffect(() => {
    if (shouldShowTutorial && !isRunning) {
      // Pequeño delay para que la página cargue primero
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [shouldShowTutorial, isRunning]);

  const handleStartTutorial = () => {
    setShowWelcome(false);
    // Pequeño delay para que el modal se cierre primero
    setTimeout(() => {
      startTutorial();
    }, 300);
  };

  const handleSkipTutorial = () => {
    setShowWelcome(false);
    endTutorial();
  };

  const contextValue: TutorialContextType = {
    isRunning,
    startTutorial: () => {
      setShowWelcome(false);
      startTutorial();
    },
    endTutorial,
    resetTutorial,
    shouldShowTutorial,
    isCompleted,
  };

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}

      {/* Modal de bienvenida */}
      {showWelcome && (
        <WelcomeModal onStart={handleStartTutorial} onSkip={handleSkipTutorial} />
      )}

      {/* Joyride tour */}
      <Joyride
        steps={steps}
        run={isRunning}
        stepIndex={stepIndex}
        callback={handleCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        spotlightClicks
        hideCloseButton
        scrollToFirstStep
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#B8860B',
            backgroundColor: '#ffffff',
            textColor: '#171717',
            arrowColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
          },
          spotlight: {
            borderRadius: 8,
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
        tooltipComponent={CustomTooltip}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: '¡Empezar!',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        floaterProps={{
          disableAnimation: false,
        }}
      />
    </TutorialContext.Provider>
  );
}

export default TutorialProvider;
